import { useEffect } from 'react';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import type { LDContext } from 'launchdarkly-js-client-sdk';
import { useObserver } from 'mobx-react-lite';
import { useLocation } from 'react-router-dom';

import { getPersistedValue } from 'shared/utils/persisted-values';
import { defaultUser } from 'shared/constants/users';
import { LD_APPLICATION_CONTEXT } from 'src/constants/launch-darkly';
import { useStores } from 'src/hooks/use-stores';
import { useUser } from 'src/hooks/use-user';
import { useUI } from 'src/hooks/use-ui';

/**
 * Extract enterpriseId from the URL pathname.
 * useParams() doesn't work at the router root level (before routes are matched),
 * so we parse the URL directly.
 */
function getEnterpriseIdFromPath(pathname: string): string | null {
  const match = /\/enterprise\/([^/]+)/.exec(pathname);
  return match ? match[1] : null;
}

export function useFeatureFlagsController(): void {
  const flags = useFlags();
  const stores = useStores();
  const ldClient = useLDClient();
  const User = useUser();
  const UI = useUI();
  const location = useLocation();

  const enterpriseIdFromPath = getEnterpriseIdFromPath(location.pathname);

  const dispensaryId = useObserver(() => UI.dispensary.id);
  const dispensaryCName = useObserver(() => UI.dispensary.cName);
  const dispensaryLoading = useObserver(() => UI.dispensaryLoading);
  const dispensaryChain = useObserver(() => UI.dispensary.chain);
  const storeEnterpriseId = useObserver(() => UI.dispensary.retailer?.enterpriseId ?? User.enterpriseId ?? null);

  // Prefer URL param, then fall back to store values
  const enterpriseId = enterpriseIdFromPath ?? storeEnterpriseId;

  const userType = useObserver(() => User.profile.type);
  const isCanaryUser = getPersistedValue(`launch-darkly-canary-user`, false);

  useEffect(() => {
    if (!ldClient) {
      return;
    }
    // userType should always be defined, but || makes typescript and failure cases happier
    const context: LDContext = {
      kind: 'multi',
      user: {
        ...defaultUser,
        ...(userType && { type: userType }),
        canary: isCanaryUser,
      },
      application: LD_APPLICATION_CONTEXT,
    };

    if (dispensaryId && dispensaryCName) {
      context['ecomm-dispensary'] = {
        key: dispensaryId,
        // leaving these here in case we want them later / to show it's possible
        // we can add arbitrary key/value pairs and target based on those "attributes"
        // cName: dispensaryCName,
        // chain: dispensaryChain || null,
      };
    }
    if (enterpriseId) {
      context['ecomm-enterprise'] = {
        key: enterpriseId,
      };
    }
    void ldClient.identify(context);
  }, [
    ldClient,
    dispensaryLoading,
    userType,
    dispensaryId,
    dispensaryChain,
    dispensaryCName,
    enterpriseId,
    isCanaryUser,
  ]);

  useEffect(() => {
    stores.FeatureFlags.setFlags(flags);
  }, [stores.FeatureFlags, flags]);
}
